import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Award, Clock, Leaf, ArrowRight, Phone, Compass, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { HeroSlider } from '../components/common/HeroSlider';
import { ProjectCard } from '../components/common/ProjectCard';
import { FilterBar } from '../components/common/FilterBar';
import { TestimonialCard } from '../components/common/TestimonialCard';
import { EnquiryForm } from '../components/common/EnquiryForm';
import { heroSlides, usps, testimonials as staticTestimonials, companyInfo } from '../data/siteData';
import { projectsApi, testimonialsApi } from '../services/api';

const iconMap = {
  Shield: Shield,
  Award: Award,
  Clock: Clock,
  Leaf: Leaf,
  Compass: Compass,
};

export default function HomePage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ location: '', type: '', budget: '' });
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState(staticTestimonials);
  const [loadingProjects, setLoadingProjects] = useState(true);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.type) params.set('type', filters.type);
    navigate(`/projects?${params.toString()}`);
  };

  useEffect(() => {
    // Fetch projects from API
    projectsApi.getAll()
      .then(res => setProjects(res.data || res || []))
      .catch(() => setProjects([]))
      .finally(() => setLoadingProjects(false));

    // Fetch testimonials from API
    testimonialsApi.getAll(false)
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setTestimonials(data);
      })
      .catch(() => {}); // silently fall back to static
  }, []);

  const featuredProjects = projects.filter((p) => p.featured);
  const ongoingProjects = projects.filter((p) => p.status === 'Ongoing');
  const upcomingProjects = projects.filter((p) => p.status === 'Upcoming');

  // Dynamic slides based on active projects (Ongoing & Upcoming)
  const activeProjects = projects.filter((p) => p.status === 'Ongoing' || p.status === 'Upcoming');
  const dynamicHeroSlides = activeProjects.length > 0
    ? activeProjects.map((p) => ({
        id: p.id,
        title: p.name,
        subtitle: p.description || `${p.category} Project in ${p.location}`,
        image: p.images && p.images[0] ? p.images[0] : 'https://images.unsplash.com/photo-1549665600-42c9e852661e?w=1920&q=80',
        cta: p.status === 'Upcoming' ? 'Learn More' : 'View Details',
        link: `/projects/${p.id}`,
      }))
    : heroSlides;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <HeroSlider slides={dynamicHeroSlides} />

      {/* Quick Search */}
      <section className="relative -mt-20 z-30 max-w-5xl mx-auto px-6">
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          showViewToggle={false}
          compact
          projects={projects}
          onSearch={handleSearch}
        />
      </section>

      {/* USP Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-slate-900 font-outfit mt-4">
              Building Trust Since 1998
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-7">
            {usps.map((usp, index) => {
              const Icon = iconMap[usp.icon];
              return (
                <div
                  key={index}
                  data-testid={`usp-${index}`}
                  className="text-center p-8 rounded-2xl bg-[#F8F9FA] hover:bg-white hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#F0F8FF] flex items-center justify-center group-hover:bg-[#47ace2] transition-colors">
                    <Icon className="w-8 h-8 text-[#47ace2] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 font-outfit mb-3">
                    {usp.title}
                  </h3>
                  <p className="text-slate-600 font-manrope">{usp.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-24 md:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
                Explore
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight text-slate-900 font-outfit mt-4">
                Featured Projects
              </h2>
            </div>
            <Link to="/projects">
              <Button
                variant="outline"
                data-testid="view-all-projects-btn"
                className="border-[#47ace2] text-[#47ace2] hover:bg-[#F0F8FF] rounded-full px-6"
              >
                View All Projects
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          {loadingProjects ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-10 h-10 text-[#47ace2] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(featuredProjects.length > 0 ? featuredProjects : projects.slice(0, 3)).map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Ongoing Projects */}
      {ongoingProjects.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
                  In Progress
                </span>
                <h2 className="text-3xl md:text-5xl font-light tracking-tight text-slate-900 font-outfit mt-4">
                  Ongoing Projects
                </h2>
              </div>
              <Link to="/projects?status=Ongoing">
                <Button
                  variant="ghost"
                  className="text-[#47ace2] hover:text-[#1f7db3]"
                >
                  See All Ongoing
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {ongoingProjects.slice(0, 2).map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
                About Modern Arch
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight font-outfit mt-4 mb-6">
                {companyInfo.tagline}
              </h2>
              <p className="text-slate-300 font-manrope text-lg leading-relaxed mb-8">
                For over 25 years, Modern Group Nagpur has been at the forefront of transforming
                the city's skyline. Our commitment to quality, transparency, and timely delivery
                has earned us the trust of thousands of happy families.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <div>
                  <span className="block text-4xl font-light text-[#47ace2] font-outfit">
                    {companyInfo.projectsCompleted}
                  </span>
                  <span className="text-slate-400 text-sm font-manrope">Projects</span>
                </div>
                <div>
                  <span className="block text-4xl font-light text-[#47ace2] font-outfit">
                    {companyInfo.happyFamilies}
                  </span>
                  <span className="text-slate-400 text-sm font-manrope">Happy Families</span>
                </div>
                <div>
                  <span className="block text-4xl font-light text-[#47ace2] font-outfit">
                    {companyInfo.sqftDelivered}
                  </span>
                  <span className="text-slate-400 text-sm font-manrope">Sq.ft Delivered</span>
                </div>
                <div>
                  <span className="block text-4xl font-light text-[#47ace2] font-outfit">
                    25+
                  </span>
                  <span className="text-slate-400 text-sm font-manrope">Years Legacy</span>
                </div>
              </div>
              <Link to="/about">
                <Button
                  data-testid="learn-more-btn"
                  className="bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full px-8"
                >
                  Learn More About Us
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7581120/pexels-photo-7581120.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Modern Arch Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#47ace2] text-white p-6 rounded-2xl shadow-xl">
                <span className="block text-3xl font-light font-outfit">Since</span>
                <span className="block text-5xl font-bold font-outfit">{companyInfo.founded}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Projects */}
      {upcomingProjects.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F0F8FF]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold font-manrope mb-4">
                Coming Soon
              </span>
              <h2 className="text-3xl md:text-5xl font-light tracking-tight text-slate-900 font-outfit">
                Upcoming Projects
              </h2>
              <p className="text-slate-600 font-manrope mt-4 max-w-2xl mx-auto">
                Be the first to know about our new launches. Pre-book now and avail exclusive early bird offers.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/upcoming">
                <Button
                  data-testid="view-upcoming-btn"
                  className="bg-[#47ace2] hover:bg-[#1f7db3] text-white rounded-full px-8"
                >
                  View All Upcoming Projects
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#47ace2] font-manrope">
              Testimonials
            </span>
            <h2 className="text-3xl md:text-5xl font-light tracking-tight text-slate-900 font-outfit mt-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#47ace2] to-[#1f7db3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <h2 className="text-3xl md:text-5xl font-light tracking-tight font-outfit mb-6">
                Ready to Find Your Dream Home?
              </h2>
              <p className="text-white/90 font-manrope text-lg mb-8">
                Get in touch with our experts today. Schedule a site visit or request a callback
                to discuss your requirements.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${companyInfo.primaryPhone || '+918766551928'}`}>
                  <Button
                    data-testid="call-now-btn"
                    className="bg-white text-[#47ace2] hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                </a>
                <Link to="/projects">
                  <Button
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                  >
                    Browse Projects
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-semibold text-slate-900 font-outfit mb-6">
                Quick Enquiry
              </h3>
              <EnquiryForm compact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
